import { BillingPaymentMethods } from '@kizen/api/billing';
import DragItem from '__components/DragAndDropLayout/DragItem';
import Icon from '__components/Kizen/Icon';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import IconButtonMenu from '__components/Kizen/IconButtonMenu';
import { colorsButton } from '__app/colors';
import { Typography } from '@kizen/kds/Typography';
import { OverlayTrigger } from 'react-bootstrap';
import Tooltip from '__components/Kizen/Tooltip';

type PaymentMethodProps = {
  element?: BillingPaymentMethods['payment_methods'][number];
  isReadOnly?: boolean;
  getMenuHandler?: (id: string) => (chosen: { value: string }) => void;
  index?: number;
};

const cardsWithIcons = [
  'visa',
  'mastercard',
  'amex',
  'discover',
  'diners',
  'jcb',
];

const brandNames: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

export const PaymentMethod = (props: PaymentMethodProps) => {
  const { element, isReadOnly, getMenuHandler, ...rest } = props;
  const { t } = useTranslation();

  const { index } = rest;

  if (!element) {
    return null;
  }

  const hasIcon = cardsWithIcons.includes(element.card?.brand);
  const iconString = hasIcon ? `cc-${element.card?.brand}` : 'credit-card';

  return (
    <DragItem {...rest} disabled={isReadOnly} hideDragger={isReadOnly}>
      <div
        className="grid grid-cols-[repeat(var(--column-count),minmax(0,1fr))] z-[1] w-full items-center"
        style={{ '--column-count': 6 } as CSSProperties}
      >
        <div className={isReadOnly ? 'col-span-3 ml-[-25px]' : 'col-span-3'}>
          <Typography size="md">
            {element.type === 'card'
              ? `${t('Credit Card')} - ${element.card.last4}${index === 0 ? ` (${t('primary')})` : ''}`
              : ''}
          </Typography>
        </div>
        <div className="">
          {element.type === 'card' ? (
            <OverlayTrigger
              overlay={
                <Tooltip
                  label={brandNames[element.card?.brand] || element.card?.brand}
                />
              }
            >
              <Icon icon={iconString} color="var(--color-background-darker)" />
            </OverlayTrigger>
          ) : (
            ''
          )}
        </div>
        <div className="">
          <Typography size="md">
            {element.type === 'card'
              ? `${element.card.exp_month}/${element.card.exp_year}`
              : ''}
          </Typography>
        </div>
        <div className="ml-auto">
          {isReadOnly ? null : (
            <IconButtonMenu
              overlay
              overlayOffsetY={10}
              position="right"
              sizing="dense"
              color={colorsButton.iconGray}
              options={[
                {
                  value: 'delete',
                  label: t('Remove'),
                },
                index !== 0
                  ? {
                      value: 'make_default',
                      label: t('Make Primary'),
                    }
                  : undefined,
              ].filter(Boolean)}
              onChange={getMenuHandler?.(element.id)}
            >
              <Icon icon="three-dot" />
            </IconButtonMenu>
          )}
        </div>
      </div>
    </DragItem>
  );
};
